'use client';

/**
 * Best-effort push cleanup on sign-out. Must run BEFORE the Supabase session
 * is torn down, since disabling server-side requires an authenticated call.
 * Unsubscribing the browser's PushSubscription (not just disabling it
 * server-side) is what actually prevents a prior account's notifications
 * from reaching whoever signs in next on the same device: a fresh
 * subscribe() call after this always yields a new endpoint, so there is
 * nothing left for a different account to accidentally inherit.
 */
export async function unsubscribeCurrentDevicePush(): Promise<void> {
  try {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;

    const endpoint = subscription.endpoint;
    await fetch('/api/push/unsubscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ endpoint }),
    }).catch(() => {});
    await subscription.unsubscribe().catch(() => {});
  } catch {
    // best-effort only -- must never block sign-out
  }
}
